import { useRef, useState } from 'react';

import type { SkuDTO } from '@invenco/products-interface';

import { Button } from '@/components/core/button';
import {
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/core/dialog';
import { useDialogContext } from '@/components/dialog-with-context';
import { SkuCombobox } from '@/pages/seller/components/sku-combobox';
import type { Result } from '@/shared/helpers/Result';

import type { OrderLineRow } from './order-line-row';

type AssignSkuDialogContentProps = {
  line: OrderLineRow;
  onAssignSku: (line: OrderLineRow, sku: SkuDTO) => Promise<void | Result<unknown>>;
};

export function AssignSkuDialogContent({ line, onAssignSku }: AssignSkuDialogContentProps) {
  const { setIsOpen } = useDialogContext();
  const [selectedSku, setSelectedSku] = useState<SkuDTO | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);

  // TODO: shouldn't be necessary once using Base UI for dialogs
  const portalContainerRef = useRef<HTMLDivElement>(null);

  async function handleAssign() {
    if (!selectedSku) return;

    setIsAssigning(true);
    try {
      const result = await onAssignSku(line, selectedSku);
      if (result?.isFailure) return;
      setIsOpen(false);
    } finally {
      setIsAssigning(false);
    }
  }

  return (
    <DialogContent className="sm:max-w-lg">
      <form
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          void handleAssign();
        }}
      >
        <DialogHeader>
          <DialogTitle>Assign SKU</DialogTitle>
          <DialogDescription>
            {line.skuName} was not recognised as a valid SKU. Please search for an existing SKU to
            replace it.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <SkuCombobox
            selectedSku={selectedSku}
            queryParams={{ archived: false }}
            portalContainerRef={portalContainerRef}
            onSelect={setSelectedSku}
            onClear={() => setSelectedSku(null)}
          />
        </DialogBody>
        <DialogFooter>
          <div ref={portalContainerRef} />
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" disabled={!selectedSku} loading={isAssigning}>
            Assign
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
